import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft, FlaskConical, ShieldCheck, Snowflake, BadgeCheck,
  Atom, Microscope, Globe, Award, TrendingUp, Beaker,
} from "lucide-react";

const STATS = [
  { value: "99%+", label: "Purity", sub: "HPLC verified" },
  { value: "500+", label: "Researchers", sub: "Trust us globally" },
  { value: "50+", label: "Peptides", sub: "In active catalog" },
  { value: "100%", label: "Research grade", sub: "Lab-certified" },
];

const VALUES = [
  {
    icon: FlaskConical,
    title: "Science First",
    body: "Every batch is HPLC tested with ≥ 99% purity. Certificates of Analysis available for every compound on request.",
  },
  {
    icon: ShieldCheck,
    title: "Third-Party Verified",
    body: "We ship nothing without independent lab confirmation. Anti-counterfeit batch codes let researchers verify authenticity in seconds.",
  },
  {
    icon: Snowflake,
    title: "Cold Chain Integrity",
    body: "Insulated, temperature-controlled shipping from our refrigerated facility (2–8 °C) all the way to your bench.",
  },
  {
    icon: Microscope,
    title: "Research Grade Only",
    body: "Our peptides and pens are intended exclusively for in-vitro and laboratory research. No medical or therapeutic claims.",
  },
];

const TIMELINE = [
  { year: "2019", title: "Founded", body: "Supreme Laboratories begins R&D for high-purity peptide synthesis." },
  { year: "2021", title: "ISO Audit Passed", body: "Facility certified to ISO-class clean-room standards." },
  { year: "2023", title: "Global Distribution", body: "Shipping cold-chain to research institutes across 40+ countries." },
  { year: "2026", title: "Pre-Filled Pens", body: "Launch of the proprietary Supreme pre-filled research pen line." },
];

export default function About() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#020409] text-white">
      <div className="border-b border-white/5 bg-black/60 backdrop-blur-md text-[11px] uppercase tracking-[0.2em] font-medium">
        <div className="max-w-7xl mx-auto px-6 py-2.5 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="about-back-btn"
            className="flex items-center gap-2 text-zinc-500 hover:text-white transition"
          >
            <ArrowLeft className="w-3 h-3 text-[#39FF14]" /> Back to Home
          </button>
        </div>
      </div>

      <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#020409]/85 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="about-logo"><Logo /></button>
          <button
            onClick={() => navigate("/products")}
            className="text-xs uppercase tracking-[0.2em] font-bold text-zinc-400 hover:text-[#39FF14] transition"
            data-testid="about-products-link"
          >
            Browse Catalog →
          </button>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(59,130,246,0.15),transparent_60%)] pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.35em] text-[#39FF14] font-bold mb-4">
              About Supreme Laboratories
            </p>
            <h1 className="font-headline text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mb-6">
              Built on{" "}
              <span className="text-[#39FF14]" style={{ textShadow: "0 0 25px rgba(57,255,20,0.55)" }}>
                science.
              </span>
              <br />Driven by{" "}
              <span className="text-blue-400 glow-blue">quality.</span>
            </h1>
            <div className="bg-[#0A0F1A] border-l-2 border-[#39FF14] rounded-r-lg px-5 py-4 max-w-xl mb-5" data-testid="about-manifesto">
              <p className="text-zinc-300 leading-relaxed mb-3">
                We created Supreme Laboratories with a simple idea:{" "}
                <span className="text-[#39FF14] font-semibold">advanced products deserve an advanced presentation</span>.
              </p>
              <p className="text-zinc-400 leading-relaxed text-sm mb-3">
                In an industry filled with generic packaging and outdated experiences, we focused on
                precision, design, and consistency. Every detail — from our pen systems to our magnetic
                presentation boxes — is developed to reflect a modern standard of quality.
              </p>
              <p className="text-zinc-400 leading-relaxed text-sm mb-3">
                Our mission is to combine premium aesthetics with a clean, professional customer experience
                inspired by luxury technology and contemporary biotech brands. We believe trust is built
                through transparency, presentation, and attention to detail.
              </p>
              <p className="text-zinc-400 leading-relaxed text-sm">
                This is why every product is approached with the same philosophy:
              </p>
              <p className="text-[#39FF14] font-bold text-sm mt-1.5 uppercase tracking-wider">
                Precision engineered · Visually refined · Professionally presented.
              </p>
            </div>
            <p className="text-zinc-300 text-lg leading-relaxed max-w-xl mb-4">
              Supreme Laboratories produces high-purity research peptides, growth factors and pre-filled
              injection pens used by laboratories, universities and independent investigators worldwide.
            </p>
            <p className="text-zinc-400 leading-relaxed max-w-xl mb-8">
              Every compound we ship is HPLC-tested, independently verified and tracked from synthesis to
              cold-chain delivery. We exist to give researchers a single trusted source — no compromises on
              purity, identity, or temperature integrity.
            </p>
            <div className="flex gap-3 flex-wrap">
              <Button
                onClick={() => navigate("/products")}
                data-testid="about-cta-products"
                className="bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider text-xs shadow-[0_0_15px_rgba(57,255,20,0.4)]"
              >
                Browse Catalog
              </Button>
              <Button
                onClick={() => navigate("/cart")}
                data-testid="about-cta-cart"
                className="bg-transparent border border-white/15 hover:border-[#39FF14]/40 text-white hover:text-[#39FF14] font-bold uppercase tracking-wider text-xs"
              >
                View Cart
              </Button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute inset-0 bg-blue-500/15 blur-[120px] rounded-full" />
            <div className="relative grid grid-cols-2 gap-4">
              {STATS.map((s, i) => (
                <div
                  key={i}
                  className="bg-[#0A0F1A] border border-white/5 rounded-2xl p-5 backdrop-blur"
                  data-testid={`about-stat-${i}`}
                >
                  <div className="font-headline text-3xl md:text-4xl font-black text-[#39FF14]"
                       style={{ textShadow: "0 0 12px rgba(57,255,20,0.45)" }}>
                    {s.value}
                  </div>
                  <div className="text-xs uppercase tracking-wider text-white font-bold mt-1.5">{s.label}</div>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mt-0.5">{s.sub}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* MISSION */}
      <section className="py-16 lg:py-20 px-6 border-b border-white/5">
        <div className="max-w-5xl mx-auto text-center">
          <p className="text-[11px] uppercase tracking-[0.35em] text-blue-400 font-bold mb-3">Our Mission</p>
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-5">
            Pure compounds.{" "}
            <span className="text-[#39FF14]" style={{ textShadow: "0 0 18px rgba(57,255,20,0.4)" }}>
              Accelerated research.
            </span>
          </h2>
          <p className="text-zinc-300 leading-relaxed max-w-3xl mx-auto">
            We believe breakthroughs in metabolic health, longevity, tissue repair and cognitive performance
            depend on the quality of the molecules that go into the experiments. Our job is to remove every
            variable but the one being studied — so every batch is identity-verified, purity-tested and
            temperature-controlled end-to-end.
          </p>
        </div>
      </section>

      {/* VALUES */}
      <section className="py-16 lg:py-20 px-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-[#39FF14] font-bold mb-3">What We Stand For</p>
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-12">
            Four non-negotiables.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
            {VALUES.map((v, i) => (
              <div
                key={i}
                data-testid={`about-value-${i}`}
                className="group relative bg-[#0A0F1A] border border-white/5 hover:border-[#39FF14]/40 rounded-xl p-5 transition-all duration-300 hover:shadow-[0_0_30px_rgba(57,255,20,0.12)]"
              >
                <div className="w-11 h-11 rounded-lg bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center mb-4">
                  <v.icon className="w-5 h-5 text-[#39FF14]" />
                </div>
                <h3 className="font-headline font-bold text-lg text-white mb-2">{v.title}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TIMELINE */}
      <section className="py-16 lg:py-20 px-6 border-b border-white/5">
        <div className="max-w-5xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.35em] text-blue-400 font-bold mb-3">The Lab Journey</p>
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-12">
            Milestones.
          </h2>
          <div className="relative">
            <div className="absolute left-[20px] md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-[#39FF14]/40 via-blue-500/40 to-transparent" />
            <div className="space-y-10">
              {TIMELINE.map((t, i) => (
                <div
                  key={t.year}
                  data-testid={`about-milestone-${i}`}
                  className={`relative grid md:grid-cols-2 gap-4 md:gap-12 ${i % 2 ? "md:[&>div:first-child]:order-2" : ""}`}
                >
                  <div className="md:text-right md:pr-8">
                    {i % 2 === 0 && (
                      <>
                        <div className="font-headline text-3xl font-black text-[#39FF14]" style={{ textShadow: "0 0 12px rgba(57,255,20,0.4)" }}>
                          {t.year}
                        </div>
                        <h3 className="font-headline font-bold text-white text-lg mt-1">{t.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mt-2">{t.body}</p>
                      </>
                    )}
                  </div>
                  <div className="md:pl-8">
                    {i % 2 === 1 && (
                      <>
                        <div className="font-headline text-3xl font-black text-[#39FF14]" style={{ textShadow: "0 0 12px rgba(57,255,20,0.4)" }}>
                          {t.year}
                        </div>
                        <h3 className="font-headline font-bold text-white text-lg mt-1">{t.title}</h3>
                        <p className="text-zinc-400 text-sm leading-relaxed mt-2">{t.body}</p>
                      </>
                    )}
                  </div>
                  <div className="absolute left-[20px] md:left-1/2 top-2 -translate-x-1/2 w-3 h-3 rounded-full bg-[#39FF14] shadow-[0_0_15px_#39FF14] border-2 border-black" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* PROMISE STRIP */}
      <section className="py-16 px-6 border-b border-white/5">
        <div className="max-w-7xl mx-auto grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {[
            { icon: BadgeCheck, label: "Anti-counterfeit code" },
            { icon: Atom, label: "≥ 99% HPLC purity" },
            { icon: Globe, label: "Worldwide shipping" },
            { icon: Beaker, label: "Lab-tested every batch" },
          ].map((p, i) => (
            <div key={i} className="flex items-center gap-3 bg-[#0A0F1A] border border-white/5 rounded-lg px-4 py-3">
              <div className="w-9 h-9 rounded-full bg-[#39FF14]/10 border border-[#39FF14]/30 flex items-center justify-center shrink-0">
                <p.icon className="w-4 h-4 text-[#39FF14]" />
              </div>
              <span className="text-sm font-semibold text-zinc-200">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <Award className="w-10 h-10 text-[#39FF14] mx-auto mb-5" />
          <h2 className="font-headline text-3xl md:text-4xl font-black tracking-tighter mb-4">
            Ready to research with{" "}
            <span className="text-[#39FF14]" style={{ textShadow: "0 0 18px rgba(57,255,20,0.4)" }}>
              Supreme
            </span>
            ?
          </h2>
          <p className="text-zinc-400 max-w-2xl mx-auto mb-8 leading-relaxed">
            Browse 59+ research peptides and pre-filled pens. Every order ships cold-chain with a unique
            batch verification code.
          </p>
          <Button
            onClick={() => navigate("/products")}
            data-testid="about-cta-footer"
            className="h-12 px-10 bg-[#39FF14] hover:bg-[#2adc0c] text-black font-bold uppercase tracking-wider shadow-[0_0_20px_rgba(57,255,20,0.4)] hover:shadow-[0_0_30px_rgba(57,255,20,0.6)]"
          >
            <TrendingUp className="w-4 h-4 mr-2" /> View Catalog
          </Button>
        </div>
      </section>
    </div>
  );
}
